import { useState, useEffect, useCallback, useRef } from "react";
import { useAuth } from "@/hooks/useAuth";

interface CarrierNumber {
  id: number;
  carrier: string;
  phone: string;
  updated_at: string;
}

const CARRIERS = ["SKT", "KT", "LGU+"] as const;
type Carrier = (typeof CARRIERS)[number];

const SUPABASE_URL = import.meta.env.VITE_PUBLIC_SUPABASE_URL as string;
const SUPABASE_ANON_KEY = import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY as string;

const EMPTY_NUMBERS: Record<Carrier, string> = { SKT: "", KT: "", "LGU+": "" };

// 탭 복귀(리로드) 시 로딩 없이 마지막 값을 즉시 보여주기 위한 캐시
const SAVED_CACHE_KEY = "cache:admin_carrier_numbers";
// 저장 전 작성 중인 입력값(초안)을 잃지 않도록 보관
const DRAFT_CACHE_KEY = "cache:admin_carrier_numbers_draft";

function normalize(map: Record<string, unknown>): Record<Carrier, string> {
  const result: Record<string, string> = { ...EMPTY_NUMBERS };
  CARRIERS.forEach((c) => {
    result[c] = typeof map[c] === "string" ? (map[c] as string) : "";
  });
  return result as Record<Carrier, string>;
}

function readCache(key: string): Record<Carrier, string> | null {
  try {
    const raw = sessionStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Record<string, unknown>;
    if (parsed && typeof parsed === "object") return normalize(parsed);
    return null;
  } catch {
    return null;
  }
}

function writeCache(key: string, value: Record<Carrier, string>): void {
  try {
    sessionStorage.setItem(key, JSON.stringify(value));
  } catch {
    // 저장 실패(용량 초과 등)는 무시
  }
}

function clearCache(key: string): void {
  try {
    sessionStorage.removeItem(key);
  } catch {
    // 삭제 실패는 무시
  }
}

function sameNumbers(a: Record<Carrier, string>, b: Record<Carrier, string>): boolean {
  return CARRIERS.every((c) => a[c] === b[c]);
}

export default function AdminCarrierNumberTab() {
  const { session } = useAuth();
  const savedCacheRef = useRef<Record<Carrier, string> | null>(readCache(SAVED_CACHE_KEY));
  const draftCacheRef = useRef<Record<Carrier, string> | null>(readCache(DRAFT_CACHE_KEY));

  const initialNumbers = draftCacheRef.current ?? savedCacheRef.current ?? EMPTY_NUMBERS;
  const initialSaved = savedCacheRef.current ?? EMPTY_NUMBERS;

  const [numbers, setNumbers] = useState<Record<Carrier, string>>(initialNumbers);
  const [savedNumbers, setSavedNumbers] = useState<Record<Carrier, string>>(initialSaved);
  // 저장되지 않은 수정이 있는지 (있으면 조용한 갱신이 입력값을 덮어쓰지 않음)
  const dirtyRef = useRef(!sameNumbers(initialNumbers, initialSaved));
  const [loading, setLoading] = useState(
    savedCacheRef.current === null && draftCacheRef.current === null
  );
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState<{ msg: string; type: "success" | "error" } | null>(null);

  const showToast = (msg: string, type: "success" | "error" = "success") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  };

  const fetchNumbers = useCallback(async () => {
    const accessToken = session?.access_token;
    if (!accessToken) return;
    // 캐시된 값이 이미 있으면 로딩 화면 없이 조용히 갱신
    if (savedCacheRef.current === null) setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${SUPABASE_URL}/functions/v1/admin-manage-carrier-numbers`, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
          apikey: SUPABASE_ANON_KEY,
          "Content-Type": "application/json",
        },
      });
      if (!res.ok) {
        const err = await res.json().catch(() => null);
        throw new Error(err?.error || `HTTP ${res.status}`);
      }
      const data = await res.json();
      const map: Record<string, string> = { ...EMPTY_NUMBERS };
      (data.numbers ?? []).forEach((n: CarrierNumber) => {
        if (map[n.carrier] !== undefined) map[n.carrier] = n.phone || "";
      });
      const next = map as Record<Carrier, string>;
      savedCacheRef.current = next;
      setSavedNumbers(next);
      writeCache(SAVED_CACHE_KEY, next);
      // 사용자가 아직 저장하지 않은 입력이 있으면 덮어쓰지 않는다
      if (!dirtyRef.current) {
        setNumbers(next);
        clearCache(DRAFT_CACHE_KEY);
      }
    } catch (err: any) {
      console.error("[AdminCarrierNumber] fetch error:", err);
      // 캐시/기존 값이 있으면 화면을 유지하고, 완전히 비었을 때만 오류 화면 표시
      if (savedCacheRef.current === null && draftCacheRef.current === null) {
        setError(err.message || "번호 목록을 불러오는 중 오류가 발생했습니다.");
      } else {
        showToast(err.message || "최신 번호를 불러오지 못했습니다.", "error");
      }
    } finally {
      setLoading(false);
    }
  }, [session]);

  useEffect(() => {
    fetchNumbers();
  }, [fetchNumbers]);

  const handleSave = async () => {
    const accessToken = session?.access_token;
    if (!accessToken) return;
    setSaving(true);
    try {
      const payload = CARRIERS.map((carrier) => ({
        carrier,
        phone: numbers[carrier].trim(),
      }));
      const res = await fetch(`${SUPABASE_URL}/functions/v1/admin-manage-carrier-numbers`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${accessToken}`,
          apikey: SUPABASE_ANON_KEY,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ numbers: payload }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => null);
        throw new Error(err?.error || `HTTP ${res.status}`);
      }
      const data = await res.json();
      const map: Record<string, string> = { ...EMPTY_NUMBERS };
      (data.numbers ?? []).forEach((n: CarrierNumber) => {
        if (map[n.carrier] !== undefined) map[n.carrier] = n.phone || "";
      });
      const next = map as Record<Carrier, string>;
      savedCacheRef.current = next;
      setNumbers(next);
      setSavedNumbers(next);
      writeCache(SAVED_CACHE_KEY, next);
      clearCache(DRAFT_CACHE_KEY);
      dirtyRef.current = false;
      showToast("수신번호가 저장되었습니다.", "success");
    } catch (err: any) {
      showToast(err.message || "저장 중 오류가 발생했습니다.", "error");
    } finally {
      setSaving(false);
    }
  };

  const updateNumber = (carrier: Carrier, value: string) => {
    setNumbers((prev) => {
      const next = { ...prev, [carrier]: value };
      dirtyRef.current = !sameNumbers(next, savedNumbers);
      // 작성 중인 초안을 보관해 탭을 옮겼다 돌아와도 유지
      if (dirtyRef.current) {
        writeCache(DRAFT_CACHE_KEY, next);
      } else {
        clearCache(DRAFT_CACHE_KEY);
      }
      return next;
    });
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-20">
        <div className="w-8 h-8 border-2 border-rose-500/40 border-t-rose-500 rounded-full animate-spin mb-3"></div>
        <p className="text-gray-500 text-sm">수신번호 불러오는 중...</p>
      </div>
    );
  }

  if (error && !savedCacheRef.current && !draftCacheRef.current) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-center">
        <i className="ri-error-warning-line text-3xl text-red-400 mb-2 block"></i>
        <p className="text-red-400 text-sm mb-3">{error}</p>
        <button
          onClick={fetchNumbers}
          className="px-4 py-2 bg-white/[0.06] hover:bg-white/[0.1] text-gray-300 rounded-lg text-sm cursor-pointer transition-colors"
        >
          다시 시도
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-white font-semibold text-lg">3사 수신번호 관리</h2>
        <p className="text-gray-500 text-sm mt-0.5">
          사용자가 3사 테스트 발송 시 사용할 수신번호를 등록합니다
        </p>
      </div>

      <div className="bg-white/[0.03] border border-white/[0.08] rounded-2xl overflow-hidden">
        <div className="px-5 py-4 border-b border-white/[0.06]">
          <div className="flex items-center gap-2">
            <i className="ri-smartphone-line text-rose-400 text-sm"></i>
            <span className="text-white text-sm font-semibold">수신번호 설정</span>
          </div>
        </div>
        <div className="p-5 space-y-4">
          {CARRIERS.map((carrier) => {
            const colors: Record<string, { bg: string; border: string; label: string; icon: string }> = {
              SKT: { bg: "bg-rose-500/10", border: "border-rose-500/20", label: "text-rose-400", icon: "ri-signal-tower-line" },
              KT: { bg: "bg-sky-500/10", border: "border-sky-500/20", label: "text-sky-400", icon: "ri-wifi-line" },
              "LGU+": { bg: "bg-emerald-500/10", border: "border-emerald-500/20", label: "text-emerald-400", icon: "ri-base-station-line" },
            };
            const c = colors[carrier];
            return (
              <div
                key={carrier}
                className={`flex items-center gap-4 rounded-xl border ${c.border} ${c.bg} px-4 py-3.5`}
              >
                <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-white/[0.06] shrink-0">
                  <i className={`${c.icon} ${c.label} text-sm`}></i>
                </div>
                <div className="flex-1 min-w-0">
                  <p className={`text-sm font-bold ${c.label}`}>{carrier}</p>
                  <p className="text-gray-500 text-xs mt-0.5">수신번호를 입력하세요</p>
                </div>
                <input
                  type="text"
                  value={numbers[carrier]}
                  onChange={(e) => updateNumber(carrier, e.target.value)}
                  placeholder="010-0000-0000"
                  className="w-44 bg-[#0a0a18] border border-white/[0.08] rounded-lg px-3 py-2 text-white text-sm placeholder-gray-600 focus:outline-none focus:border-rose-500/40"
                />
              </div>
            );
          })}

          <div className="flex items-center justify-between pt-2">
            <div className="flex items-start gap-2 text-xs text-gray-500">
              <i className="ri-information-line text-rose-400 mt-0.5 shrink-0"></i>
              <p className="leading-relaxed">
                등록된 번호는 사용자 3사 테스트 페이지에 마스킹되어 표시됩니다.<br />
                사용자는 중간 4자리를 확인할 수 없으며, 관리자가 지정한 번호로만 발송 가능합니다.
              </p>
            </div>
            <button
              onClick={handleSave}
              disabled={saving}
              className="px-5 py-2.5 rounded-xl bg-gradient-to-r from-rose-600 to-orange-600 text-white text-sm font-semibold hover:from-rose-500 hover:to-orange-500 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer transition-all whitespace-nowrap shrink-0 ml-4"
            >
              {saving ? (
                <span className="flex items-center gap-2">
                  <i className="ri-loader-4-line animate-spin text-sm"></i>
                  저장 중...
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  <i className="ri-save-line text-sm"></i>
                  저장
                </span>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Toast */}
      {toast && (
        <div
          className={`fixed bottom-6 right-6 flex items-center gap-3 px-4 py-3 rounded-xl border shadow-lg z-[100] transition-all ${
            toast.type === "success"
              ? "bg-[#1a1a2e] border-emerald-500/30 text-emerald-400"
              : "bg-[#1a1a2e] border-red-500/30 text-red-400"
          }`}
        >
          <div className="w-5 h-5 flex items-center justify-center">
            <i
              className={`${
                toast.type === "success" ? "ri-checkbox-circle-line" : "ri-close-circle-line"
              } text-base`}
            ></i>
          </div>
          <span className="text-sm">{toast.msg}</span>
        </div>
      )}
    </div>
  );
}